import type { ArtifactListingEntry, ConversationCard } from '@crowdex/agent-contracts/api';
import type { ElementSelection } from '@crowdex/plugin-contracts';
import type { WidgetState } from '@crowdex/ui/widgets';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

/**
 * What `/desktop-preview/chat?board=…` shows that no server gives it (the redesign's boards):
 * the cards of the conversation and their widgets' data, a stopped turn, the base's new commits.
 * Only the preview writes it; everywhere else it stays empty and changes nothing.
 */
export type DesktopPreviewState = {
  cards: readonly ConversationCard[] | null;
  widgetData: Readonly<Record<string, WidgetState>>;
  turnFailure: string | null;
  branchBehind: number | null;
  /** The conversation's artifacts, and what each shows (a page), for the artifact boards. */
  artifacts: readonly ArtifactListingEntry[] | null;
  artifactHtml: Readonly<Record<string, string>>;
  /** The elements pointed at in each artifact's view, as its app attached them. */
  artifactPointed: Readonly<Record<string, readonly ElementSelection[]>>;
  /** The variants each artifact's view offers ("A · Bold"), the first one shown. */
  artifactVariants: Readonly<Record<string, readonly string[]>>;
  /** The board's conversation runs in the cloud (its offline notice says so). */
  inCloud: boolean;
  /** The board's conversation runs in an empty cloud sandbox (no repository). */
  emptySandbox: boolean;
  /** The board's turn still runs: sent and not answered yet, or streaming its last message. */
  chatStatus: 'submitted' | 'streaming' | null;
};

export const desktopPreviewState = createStore<DesktopPreviewState>(() => ({
  cards: null,
  widgetData: {},
  turnFailure: null,
  branchBehind: null,
  artifacts: null,
  artifactHtml: {},
  artifactPointed: {},
  artifactVariants: {},
  inCloud: false,
  emptySandbox: false,
  chatStatus: null,
}));

export function useDesktopPreviewState<T>(selector: (state: DesktopPreviewState) => T): T {
  return useStore(desktopPreviewState, selector);
}
